/**
 * Snap height:
 * - number 0–1 → fraction of viewport height
 * - number > 1 → pixels
 * - string → `"148px"` or `"30rem"` only
 * Unknown strings (including `"50%"`) are ignored.
 */
export type SnapPoint = number | string;

export type ResolvedSnapPoints = {
  /** Original snap values, sorted by ascending height. */
  points: SnapPoint[];
  /** Heights in px, same order as `points`. */
  heights: number[];
  /** translateY offsets from the top snap: `topHeight - height_i`. */
  offsets: number[];
  topHeight: number;
};

export function parseSnapPointToPx(
  snap: SnapPoint,
  viewportHeight: number,
  rootFontSize: number,
): number | null {
  if (typeof snap === "number") {
    if (!Number.isFinite(snap)) {
      return null;
    }
    return snap <= 1 ? snap * viewportHeight : snap;
  }

  const trimmed = snap.trim();
  const pxMatch = /^(-?[\d.]+)px$/i.exec(trimmed);
  if (pxMatch) {
    const value = parseFloat(pxMatch[1]);
    return Number.isFinite(value) ? value : null;
  }

  const remMatch = /^(-?[\d.]+)rem$/i.exec(trimmed);
  if (remMatch) {
    const value = parseFloat(remMatch[1]);
    return Number.isFinite(value) ? value * rootFontSize : null;
  }

  return null;
}

export function resolveSnapPoints(
  snapPoints: SnapPoint[],
  viewportHeight: number,
  rootFontSize: number,
): ResolvedSnapPoints {
  const withHeights = snapPoints
    .map(point => {
      const height = parseSnapPointToPx(point, viewportHeight, rootFontSize);
      if (height === null || !Number.isFinite(height)) {
        return null;
      }
      return { point, height };
    })
    .filter((entry): entry is { point: SnapPoint; height: number } => entry !== null);

  withHeights.sort((a, b) => a.height - b.height);

  const topHeight = withHeights.length > 0 ? withHeights[withHeights.length - 1].height : 0;

  return {
    points: withHeights.map(entry => entry.point),
    heights: withHeights.map(entry => entry.height),
    offsets: withHeights.map(entry => topHeight - entry.height),
    topHeight,
  };
}
